"use client";

import { useState } from "react";
import Image from "next/image";
import PageContent from "@/components/page-content";
import SectionHeader from "@/components/landing/section-header";
import { Workflow, Copy, Check, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Link from "next/link";

type CaptionLength = "short" | "normal" | "long";

const captionData: Record<CaptionLength, { caption: string; responseTimeMs: number; outputTokens: number }> = {
  short: {
    caption: "A brown dog holds a frisbee on a sidewalk next to yellow flowers.",
    responseTimeMs: 245,
    outputTokens: 14,
  },
  normal: {
    caption: "A brown dog wearing a blue harness and leash holds a green and blue frisbee in its mouth while standing on a gray sidewalk. A bouquet of yellow flowers is visible nearby, adding color to the urban scene.",
    responseTimeMs: 385,
    outputTokens: 42,
  },
  long: {
    caption: "A brown dog wearing a blue harness and leash stands on a gray concrete sidewalk, proudly holding a green and blue frisbee in its mouth. The dog appears to be a medium-sized breed with a short coat and alert, happy expression. Its harness is secured around its chest, with the leash trailing behind. To the side of the dog, a vibrant bouquet of yellow flowers adds a splash of color to the otherwise neutral-toned urban setting. The sidewalk shows typical wear patterns, and the overall composition suggests this is a moment captured during an outdoor walk or play session in a residential or park area.",
    responseTimeMs: 620,
    outputTokens: 112,
  },
};

const inputTokens = 729;
const imageSrc = "/images/dog_caption_example.png";

const codeExamples = {
  python: `import moondream as md
from PIL import Image

# Initialize with API key
model = md.vl(api_key="your-api-key")

# Load your image
image = Image.open("photo.jpg")

# Short caption (~25 words) - fast labels
short = model.caption(image, length="short")
print(short["caption"])

# Normal caption (~80 words) - balanced detail
normal = model.caption(image, length="normal")
print(normal["caption"])

# Long caption (~180 words) - comprehensive
long = model.caption(image, length="long")
print(long["caption"])`,

  nodejs: `import { vl } from 'moondream';
import fs from 'fs';

// Initialize with API key
const model = new vl({ apiKey: "your-api-key" });

// Load your image
const image = fs.readFileSync("photo.jpg");

// Short caption (~25 words) - fast labels
const short = await model.caption({
  image: image,
  length: "short"
});

// Normal caption (~80 words) - balanced detail
const normal = await model.caption({
  image: image,
  length: "normal"
});

// Long caption (~180 words) - comprehensive
const long = await model.caption({
  image: image,
  length: "long"
});`,

  curl: `# Short caption (~25 words)
curl -X POST https://api.moondream.ai/v1/caption \\
  -H 'X-Moondream-Auth: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{"image_url": "data:image/jpeg;base64,...", "length": "short"}'

# Normal caption (~80 words)
curl -X POST https://api.moondream.ai/v1/caption \\
  -H 'X-Moondream-Auth: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{"image_url": "data:image/jpeg;base64,...", "length": "normal"}'

# Long caption (~180 words)
curl -X POST https://api.moondream.ai/v1/caption \\
  -H 'X-Moondream-Auth: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{"image_url": "data:image/jpeg;base64,...", "length": "long"}'`,
};

export function HowItWorks() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedLength, setSelectedLength] = useState<CaptionLength>("normal");

  const currentData = captionData[selectedLength];

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(key);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const calculateCost = (inTokens: number, outTokens: number): string => {
    const inputCost = (inTokens / 1_000_000) * 0.3;
    const outputCost = (outTokens / 1_000_000) * 2.5;
    const totalCost = inputCost + outputCost;
    return `$${totalCost.toFixed(6)}`;
  };

  const playgroundLink = `/c/playground?image=${encodeURIComponent(imageSrc)}&skill=caption`;

  return (
    <PageContent>
      <div className="space-y-6">
        <SectionHeader
          sectionIcon={<Workflow className="size-4" />}
          section="How it works"
          heading="How captioning works"
          description="Upload an image and choose your caption length: short for fast labels, normal for balanced detail, or long for comprehensive descriptions."
        />

        <Card className="overflow-hidden">
          <CardContent className="p-6">
            {/* Image + Output Layout */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Image with Glassmorphism Caption Overlay */}
              <div className="lg:w-1/2 shrink-0">
                <div className="relative rounded-xl overflow-hidden">
                  <Image
                    src={imageSrc}
                    alt="Dog with frisbee"
                    width={500}
                    height={400}
                    className="w-full object-cover"
                  />
                  
                  {/* Top gradient */}
                  <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
                  
                  {/* Glassmorphism Caption Overlay */}
                  <div className="absolute top-3 left-3 right-3">
                    <div className="bg-white/15 backdrop-blur-md rounded-lg border border-white/25 px-3 py-2 shadow-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-amber-300 text-[10px] font-semibold uppercase tracking-wider">Caption</span>
                        <span className="text-white/50 text-[10px] uppercase tracking-wider">• {selectedLength}</span>
                      </div>
                      <p className="text-white font-medium text-xs leading-relaxed line-clamp-3">
                        {currentData.caption}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Output Panel */}
              <div className="lg:w-1/2 flex flex-col gap-4">
                {/* Length Toggle */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-muted-foreground">Length:</span>
                  <div className="inline-flex items-center rounded-lg bg-muted p-1">
                    {(["short", "normal", "long"] as const).map((length) => (
                      <button
                        key={length}
                        onClick={() => setSelectedLength(length)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                          selectedLength === length
                            ? "bg-background shadow-sm text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {length.charAt(0).toUpperCase() + length.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Response */}
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Output
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {currentData.responseTimeMs}ms •{" "}
                        {inputTokens + currentData.outputTokens} tokens •{" "}
                        {calculateCost(inputTokens, currentData.outputTokens)}
                      </div>
                      <Button asChild size="sm">
                        <Link href={playgroundLink}>
                          Try it
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                  <Card className="bg-muted/50 flex-1">
                    <CardContent className="p-4">
                      <pre className="text-sm whitespace-pre-wrap font-mono">
{`{
  "caption": "${currentData.caption}"
}`}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Code Examples */}
            <div className="mt-6 space-y-2">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Code
              </h3>
              <Tabs defaultValue="python" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="python">Python</TabsTrigger>
                  <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                  <TabsTrigger value="curl">cURL</TabsTrigger>
                </TabsList>

                {(["python", "nodejs", "curl"] as const).map((lang) => {
                  const code = codeExamples[lang];
                  const copyKey = `how-it-works-${lang}`;

                  return (
                    <TabsContent key={lang} value={lang} className="mt-4">
                      <div className="relative">
                        <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm border">
                          <code
                            className={`language-${lang === "nodejs" ? "javascript" : lang === "curl" ? "bash" : lang}`}
                          >
                            {code}
                          </code>
                        </pre>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 h-8 w-8 p-0"
                          onClick={() => copyToClipboard(code, copyKey)}
                        >
                          {copiedCode === copyKey ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContent>
  );
}
